import React, { useContext } from "react"
import { get, useFormState } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { generateUUID } from "shared/lib/generateUUID"
import { stopPropagation } from "shared/lib/stopPropagation"
import { useEvent } from "shared/lib/useEvent"
import { useFormValidationMessage } from "shared/lib/useFormValidationMessage"
import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { InfoTip, InfoTipProps } from "shared/ui/info-tip/InfoTip"
import { BoxSelectLoader } from "shared/ui/input/select/box/BoxSelectLoader"
import { LabelProps, Label as UIKitLabel } from "shared/ui/label/Label"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

import { InputContextProvider } from "./InputContextProvider"
import { useInputDescriptionElement } from "./accessibility/useInputDescriptionElement"

/**
 * Props that can be passed by the user of the Input component
 */
export interface InputContextUserProps {
  id?: string
  name?: string
  label?: string
  readonlyLabel?: string
  readonly?: boolean
  required?: boolean
  loading?: boolean
  disabled?: boolean
  autoFocus?: boolean
  hasError?: boolean
  hasWarning?: boolean
  unit?: string
}

/**
 * Full context value including both user props and internally-managed values
 */
export interface InputContextType
  extends Omit<
    InputContextUserProps,
    "name" | "label" | "readonlyLabel" | "unit"
  > {
  // User props with defaults applied
  name: string
  label: string
  readonlyLabel: string
  unit?: string

  // Ref management
  inputRef: React.RefObject<HTMLElement | null> | null

  // Description management (for aria-describedby)
  descriptionIDs: string[]
  registerDescription?: (id: string) => void
  unregisterDescription?: (id: string) => void
}

export interface InputRootProps
  extends InputContextUserProps,
    Omit<
      React.PropsWithoutRef<React.HTMLProps<HTMLDivElement>>,
      "label" | "name" | "value"
    > {
  inputID?: string
}

export const InputContext = React.createContext<InputContextType>({
  name: "",
  label: "",
  readonlyLabel: "",
  id: generateUUID(),
  inputRef: null,
  descriptionIDs: [],
  unit: undefined
})

export enum InputChangeBehavior {
  CHANGE = "CHANGE",
  BLUR = "BLUR"
}

const RootBase = React.memo(
  React.forwardRef(
    (
      {
        className,
        children,
        name,
        label,
        inputID,
        readonly,
        required,
        loading,
        disabled,
        autoFocus,
        hasError,
        hasWarning,
        unit,
        ...props
      }: InputRootProps,
      ref?: React.ForwardedRef<HTMLDivElement>
    ) => {
      return (
        <InputContextProvider
          name={name}
          label={label}
          id={inputID}
          readonly={readonly}
          required={required}
          loading={loading}
          disabled={disabled}
          autoFocus={autoFocus}
          hasError={hasError}
          hasWarning={hasWarning}
          unit={unit}
        >
          <div
            {...props}
            ref={ref}
            className={classNames(
              "input",
              {
                readonly,
                error: hasError,
                warning: hasWarning,
                disabled,
                loading
              },
              className
            )}
          >
            {children}
          </div>
        </InputContextProvider>
      )
    }
  )
)

const HookFormRoot = React.memo(
  React.forwardRef(
    (props: InputRootProps, ref?: React.ForwardedRef<HTMLDivElement>) => {
      const { name, hasError } = props
      const { errors } = useFormState({ name })
      const error = !!get(errors, name)

      return <RootBase {...props} ref={ref} hasError={error || hasError} />
    }
  )
)

const Root = React.memo(
  React.forwardRef(
    (props: InputRootProps, ref?: React.ForwardedRef<HTMLDivElement>) => {
      const { name } = props

      if (name) {
        return <HookFormRoot {...props} ref={ref} />
      }
      return <RootBase {...props} ref={ref} />
    }
  )
)

const Label = React.memo(
  ({
    className,
    children,
    variant,
    showUnit,
    ...props
  }: LabelProps & { showUnit?: boolean }) => {
    const { t } = useTranslation()
    const { id, label, readonlyLabel, required, readonly, unit } =
      React.useContext(InputContext)
    const labelVariant = readonly ? "subtle" : "bold"
    const displayLabel = readonly ? readonlyLabel : label

    let labelText = children || displayLabel
    if (labelText && unit && showUnit && !readonly) {
      const inText = t("Unit.In")
      labelText = `${labelText} ${inText} ${unit}`
    }

    return (
      <UIKitLabel
        {...props}
        htmlFor={readonly ? undefined : id}
        variant={variant || labelVariant}
        className={classNames("input__label", className)}
      >
        {labelText}
        {labelText && required && !readonly ? "*" : null}
      </UIKitLabel>
    )
  }
)

interface LabelContainerProps {
  className?: string
  children?: React.ReactNode
}

const LabelContainer = React.memo(
  ({ className, children }: LabelContainerProps) => {
    return (
      <div className={classNames("input__label-container", className)}>
        {children}
      </div>
    )
  }
)

interface InputMessageProps
  extends React.PropsWithoutRef<React.HTMLProps<HTMLDivElement>> {
  variant: "info" | "error" | "warning"
}

const InputMessage = React.memo(
  React.forwardRef(
    (
      { className, children, variant, ...rest }: InputMessageProps,
      ref: React.ForwardedRef<HTMLDivElement>
    ) => {
      const { id } = useInputDescriptionElement(rest.id)
      return (
        <div
          {...rest}
          id={id}
          className={classNames(
            "input__message",
            `input__message--${variant}`,
            className
          )}
          data-testid={`${variant}-message`}
          ref={ref}
        >
          {children}
        </div>
      )
    }
  )
)

export interface ErrorMessageProps<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ContextType extends Record<string, any> = Record<string, any>
> extends React.PropsWithoutRef<React.HTMLProps<HTMLDivElement>> {
  context?: ContextType
  children?: React.ReactNode
}

function ErrorMessageBaseComponent<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ContextType extends Record<string, any> = Record<string, any>
>(
  { children, context, ...props }: ErrorMessageProps<ContextType>,
  ref?: React.ForwardedRef<HTMLDivElement>
) {
  if (!children) {
    return null
  }

  return (
    <InputMessage ref={ref} variant="error" {...props}>
      {children}
    </InputMessage>
  )
}

const ErrorMessageBase = React.memo(React.forwardRef(ErrorMessageBaseComponent))

export const HookFormErrorMessage = React.memo(
  React.forwardRef(
    (
      { className, children, ...props }: ErrorMessageProps,
      ref?: React.ForwardedRef<HTMLDivElement>
    ) => {
      const { name, label } = React.useContext(InputContext)
      const { errors } = useFormState({ name })
      const getMessage = useFormValidationMessage()

      const message = children || getMessage(get(errors, name), label)

      if (!message) {
        return null
      }

      return (
        <ErrorMessageBase {...props} ref={ref}>
          {message}
        </ErrorMessageBase>
      )
    }
  )
)

const WarningMessage = React.memo(
  (props: Omit<InputMessageProps, "variant">) => {
    return <InputMessage variant="warning" {...props} />
  }
)

const ErrorMessage = React.memo(
  React.forwardRef(
    (props: ErrorMessageProps, ref?: React.ForwardedRef<HTMLDivElement>) => {
      const { name } = React.useContext(InputContext)

      if (name) {
        return <HookFormErrorMessage {...props} ref={ref} />
      }
      return <ErrorMessageBase {...props} ref={ref} />
    }
  )
)

const InfoMessage = React.memo((props: Omit<InputMessageProps, "variant">) => {
  return <InputMessage variant="info" {...props} />
})

const Affix = React.memo(
  ({
    className,
    children,
    ...props
  }: React.PropsWithoutRef<React.HTMLProps<HTMLDivElement>>) => {
    return (
      <div {...props} className={classNames("input__affix", className)}>
        {children}
      </div>
    )
  }
)

const Loader = React.memo(
  ({
    className,
    children,
    variant = "default",
    ...props
  }: React.PropsWithoutRef<React.HTMLProps<HTMLDivElement>> & {
    variant?: "default" | "box"
  }) => {
    if (variant === "box") {
      return (
        <div {...props} className={classNames("input", className)}>
          <Label isLoading />
          <BoxSelectLoader
            className={classNames("input__control", "input__loader")}
          />
        </div>
      )
    }

    return (
      <div className={classNames("input", className)}>
        <Label isLoading />
        <Skeleton
          containerClassName={classNames("input__loader", "input__control")}
        />
      </div>
    )
  }
)

const InputInfoTip = React.memo(
  ({ className, content, ...props }: InfoTipProps) => {
    const { id } = useInputDescriptionElement()

    if (!content) return null

    return (
      <>
        <InfoTip
          {...props}
          content={content}
          className={classNames("input__info-tip", className)}
        />
        <div id={id} className="visually-hidden">
          {content}
        </div>
      </>
    )
  }
)

export interface InputClearProps {
  onClick: () => void
  value?: unknown
  shouldHide?: boolean
  label?: string
  className?: string
}

const InputClear = React.memo(
  React.forwardRef<HTMLButtonElement, InputClearProps>(
    (
      { onClick, value, shouldHide = true, label: labelProp, className },
      ref
    ) => {
      const { t } = useTranslation()
      const { label: contextLabel } = useContext(InputContext)

      const label = labelProp ?? contextLabel

      const handleClick = useEvent((e: React.MouseEvent) => {
        e.stopPropagation()
        onClick()
      })

      const ariaLabel = label
        ? t("Select.ClearField", { field: label })
        : t("Select.ClearSelection")

      if (shouldHide && !value) {
        return null
      }

      return (
        <button
          type="button"
          className={classNames("input__clear", className)}
          onClick={handleClick}
          onMouseDown={stopPropagation}
          aria-label={ariaLabel}
          ref={ref}
        >
          <Icon name={IconName.CLOSE_THICK} size={IconSize.XS} />
        </button>
      )
    }
  )
)
InputClear.displayName = "InputClear"

const Input = {
  Root,
  Label,
  LabelContainer,
  ErrorMessage,
  WarningMessage,
  InfoMessage,
  InfoTip: InputInfoTip,
  Affix,
  Loader,
  Clear: InputClear
}

export { Input }
export { InputContextProvider } from "./InputContextProvider"
